import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from './userContext';

export const MessageContext = createContext();

const MessageProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const { currentUser } = useContext(UserContext);

  useEffect(() => {
    if (currentUser?.token) {
      fetchConversations();
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Fetch user's conversations
  const fetchConversations = async () => {
    if (!currentUser?.token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/messages/conversations`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread message count
  const fetchUnreadCount = async () => {
    if (!currentUser?.token) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/messages/unread-count`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Load messages for a specific conversation
  const loadConversation = async (conversationId) => {
    if (!currentUser?.token || !conversationId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/messages/conversations/${conversationId}`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      setMessages(response.data.messages);
      setActiveConversation(conversationId);
      
      // Mark messages as read
      await markConversationAsRead(conversationId);
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark conversation messages as read
  const markConversationAsRead = async (conversationId) => {
    if (!currentUser?.token || !conversationId) return;
    
    try {
      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/messages/read/${conversationId}`,
        {},
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      // Update local state
      setConversations(prev => 
        prev.map(conv => 
          conv._id === conversationId 
            ? { ...conv, unreadCount: 0 } 
            : conv
        )
      );
      
      // Recalculate total unread count
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  // Send a message
  const sendMessage = async (recipientId, content, media = null) => {
    if (!currentUser?.token || !recipientId) return null;
    
    try {
      const formData = new FormData();
      formData.append('recipientId', recipientId);
      formData.append('content', content);
      
      if (media) {
        formData.append('media', media);
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/messages`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${currentUser.token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Add new message to state if we have the active conversation
      if (activeConversation) {
        setMessages(prev => [...prev, response.data]);
      }
      
      // Refresh conversations to show the latest message
      fetchConversations();
      
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      return null;
    }
  };

  // Delete a message
  const deleteMessage = async (messageId) => {
    if (!currentUser?.token || !messageId) return false;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/messages/${messageId}`,
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );
      
      // Update local state
      setMessages(prev => 
        prev.filter(message => message._id !== messageId)
      );
      
      return true;
    } catch (error) {
      console.error('Error deleting message:', error);
      return false;
    }
  };

  return (
    <MessageContext.Provider 
      value={{ 
        conversations, 
        activeConversation,
        messages, 
        unreadCount, 
        loading, 
        fetchConversations, 
        loadConversation,
        markConversationAsRead,
        sendMessage,
        deleteMessage
      }}
    >
      {children}
    </MessageContext.Provider>
  );
};

export default MessageProvider;